/**
 * Animations d'entrée au défilement, reprises du thème wp-emilie-lanchon. Les
 * noms de classes sont ceux de la source, pour que les correctifs circulent dans
 * les deux sens entre les projets qui partagent ce système.
 *
 * Deux mécanismes indépendants :
 *
 *   - `animate` : le bloc entre quand il atteint la fenêtre. Le balisage n'écrit
 *     que la classe d'appel ; c'est ici qu'elle devient `animated`, la classe qui
 *     cache, si bien que l'état masqué n'existe que lorsque ce script est là pour
 *     le défaire.
 *   - `line-reveal` : le texte est découpé en ses lignes visibles, qui montent
 *     chacune de derrière son masque.
 *
 * Le second se retire de lui-même sous mouvement réduit, et les deux faute
 * d'`IntersectionObserver`.
 */

const mouvementReduit = () =>
	window.matchMedia?.( '(prefers-reduced-motion: reduce)' ).matches === true;

/* --- Entrée d'un bloc -------------------------------------------------- */

let observateur = null;

const armer = ( bloc ) => {
	bloc.classList.replace( 'animate', 'animated' );

	/*
	 * On observe un ancêtre non transformé quand il y en a un : surveiller le bloc
	 * lui-même ne marche pas dès que sa translation le sort de l'écran — un
	 * `translateY(100%)` ne rentrerait jamais dans le champ de l'observateur.
	 */
	observateur.observe( bloc.closest( '.animation-wrapper' ) || bloc );
};

/** Arme les blocs qui portent encore la classe d'appel. */
const armerTous = ( racine ) => {
	if ( ! observateur ) {
		return;
	}

	racine.querySelectorAll( '.animate' ).forEach( armer );
};

const initBlocs = () => {
	if ( ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	observateur = new window.IntersectionObserver(
		( entrees ) => {
			entrees.forEach( ( entree ) => {
				if ( ! entree.isIntersecting ) {
					return;
				}

				// L'élément observé peut être l'enveloppe : c'est le bloc qu'on révèle.
				const bloc = entree.target.classList.contains( 'animated' )
					? entree.target
					: entree.target.querySelector( '.animated' );

				bloc?.classList.add( 'intersecting' );
				observateur.unobserve( entree.target );
			} );
		},
		/*
		 * La marge basse retient les entrées tant que l'élément ne fait que dépasser
		 * du pli : sur mobile la barre d'adresse fait différer la fenêtre réelle du
		 * `100vh` du CSS, et la section suivante s'animait au chargement.
		 */
		{ threshold: 0, rootMargin: '0px 0px -15% 0px' }
	);

	armerTous( document );

	/*
	 * Le routeur de l'Interactivity API remplace des régions entières sans
	 * recharger — filtres, pagination. Ce qui arrive ainsi n'a jamais connu le
	 * chargement de la page : blocs comme textes découpés sont pris à l'insertion.
	 */
	new window.MutationObserver( ( mutations ) => {
		mutations.forEach( ( mutation ) => {
			mutation.addedNodes.forEach( ( noeud ) => {
				if ( noeud.nodeType !== 1 ) {
					return;
				}

				if ( noeud.classList.contains( 'animate' ) ) {
					armer( noeud );
				}

				if ( noeud.classList.contains( 'line-reveal' ) ) {
					preparerLignes( noeud );
				}

				armerTous( noeud );
				noeud.querySelectorAll( '.line-reveal' ).forEach( preparerLignes );
			} );
		} );
	} ).observe( document.body, { childList: true, subtree: true } );
};

/* --- Entrée ligne à ligne ---------------------------------------------- */

/**
 * Éléments qui portent les lignes : les enfants de niveau bloc qui ont du texte,
 * ou l'élément lui-même quand il ne contient que du texte.
 *
 * @param {Element} el Élément à découper.
 * @return {Element[]} Porteurs de lignes.
 */
const porteurs = ( el ) => {
	const blocs = Array.from( el.children ).filter(
		( enfant ) =>
			/^(P|H[1-6]|LI|DIV|BLOCKQUOTE|FIGCAPTION)$/.test( enfant.tagName ) &&
			enfant.textContent.trim()
	);

	return blocs.length ? blocs : [ el ];
};

/**
 * Enveloppe chaque mot dans un span, en descendant dans les éléments en ligne
 * (`<em>`, `<a>`…) pour ne toucher ni leur structure ni le retour à la ligne.
 * C'est ce qui permet ensuite de mesurer où les lignes se coupent.
 *
 * @param {Node} noeud Nœud à parcourir.
 */
const envelopperMots = ( noeud ) => {
	Array.from( noeud.childNodes ).forEach( ( enfant ) => {
		if ( enfant.nodeType === 3 ) {
			const fragment = document.createDocumentFragment();

			enfant.textContent.split( /(\s+)/ ).forEach( ( part ) => {
				if ( ! part ) {
					return;
				}

				if ( /^\s+$/.test( part ) ) {
					fragment.appendChild( document.createTextNode( part ) );
					return;
				}

				const mot = document.createElement( 'span' );
				mot.className = 'lr-w';
				mot.textContent = part;
				fragment.appendChild( mot );
			} );

			enfant.replaceWith( fragment );
		} else if ( enfant.nodeType === 1 ) {
			envelopperMots( enfant );
		}
	} );
};

/**
 * Découpe un porteur en lignes masquées, numérotées à partir de `depuis`.
 *
 * Le balisage en ligne survit : une ligne qui traverse un `<em>` ou un `<a>` le
 * fait scinder entre les deux lignes par `Range.extractContents()`.
 *
 * @param {Element} porteur Élément à découper.
 * @param {number}  depuis  Index de la première ligne.
 * @return {number} Index suivant.
 */
const decouper = ( porteur, depuis ) => {
	envelopperMots( porteur );

	const mots = Array.from( porteur.querySelectorAll( '.lr-w' ) );

	if ( ! mots.length ) {
		return depuis;
	}

	// Les mots se regroupent en lignes visibles d'après leur sommet mesuré.
	const groupes = [];
	let sommet = null;

	mots.forEach( ( mot ) => {
		const haut = mot.getBoundingClientRect().top;

		if ( sommet === null || haut - sommet > 2 ) {
			groupes.push( { premier: mot, dernier: mot } );
			sommet = haut;
		} else {
			groupes[ groupes.length - 1 ].dernier = mot;
		}
	} );

	const lignes = groupes.map( ( groupe, i ) => {
		const plage = document.createRange();
		plage.setStartBefore( groupe.premier );
		plage.setEndAfter( groupe.dernier );

		const interne = document.createElement( 'span' );
		interne.className = 'line__i';
		interne.style.setProperty( '--line', String( depuis + i ) );
		interne.appendChild( plage.extractContents() );

		/*
		 * L'espace de la coupure se trouve entre deux lignes et se perd : le texte se
		 * lirait « lavigne ». Sur une ligne en `nowrap`, un espace final pend hors du
		 * masque et ne coûte rien à l'œil.
		 */
		if ( i < groupes.length - 1 ) {
			interne.appendChild( document.createTextNode( ' ' ) );
		}

		const ligne = document.createElement( 'span' );
		ligne.className = 'line';
		ligne.appendChild( interne );

		return ligne;
	} );

	porteur.replaceChildren( ...lignes );

	return depuis + groupes.length;
};

const decouperElement = ( el ) => {
	const hotes = porteurs( el );
	let index = 0;

	hotes.forEach( ( hote ) => {
		if ( hote.lineRevealSource === undefined ) {
			hote.lineRevealSource = hote.innerHTML;
		}

		// On repart du balisage d'origine pour que la mesure reste juste.
		hote.innerHTML = hote.lineRevealSource;
		index = decouper( hote, index );
	} );

	return hotes;
};

let observateurLignes = null;

/** Découpe un élément et le met sous observation. */
const preparerLignes = ( el ) => {
	if ( ! observateurLignes || el.lineRevealHosts ) {
		return;
	}

	el.lineRevealHosts = decouperElement( el );
	observateurLignes.observe( el );
};

const initLignes = () => {
	if ( ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	observateurLignes = new window.IntersectionObserver(
		( entrees ) => {
			entrees.forEach( ( entree ) => {
				if ( ! entree.isIntersecting ) {
					return;
				}

				entree.target.classList.add( 'is-revealed' );
				observateurLignes.unobserve( entree.target );
			} );
		},
		{ threshold: 0, rootMargin: '0px 0px -20% 0px' }
	);

	/*
	 * On ne mesure qu'une fois les polices prêtes : les coupures de ligne diffèrent
	 * entre la police de repli et la vraie, et découper trop tôt désajuste les
	 * masques, qui sont en `nowrap`.
	 */
	const demarrer = () =>
		document.querySelectorAll( '.line-reveal' ).forEach( preparerLignes );

	if ( document.fonts?.ready ) {
		document.fonts.ready.then( demarrer );
	} else {
		demarrer();
	}

	/*
	 * Où les lignes se coupent dépend de la largeur : on redécoupe au
	 * redimensionnement. Les éléments déjà entrés se redécoupent sans rejouer.
	 */
	let minuteur = null;

	window.addEventListener( 'resize', () => {
		clearTimeout( minuteur );

		minuteur = setTimeout( () => {
			document.querySelectorAll( '.line-reveal' ).forEach( ( el ) => {
				if ( ! el.lineRevealHosts ) {
					return;
				}

				const revele = el.classList.contains( 'is-revealed' );

				if ( revele ) {
					el.classList.add( 'is-instant' );
				}

				el.lineRevealHosts = decouperElement( el );

				if ( revele ) {
					requestAnimationFrame( () => el.classList.remove( 'is-instant' ) );
				}
			} );
		}, 150 );
	} );
};

let demarre = false;

/** Met en place les deux mécanismes. Appelable une seule fois. */
export function initAnimations() {
	if ( demarre || typeof window === 'undefined' ) {
		return;
	}

	demarre = true;

	/*
	 * Les lignes d'abord : `initBlocs` installe le MutationObserver qui prend en
	 * charge les deux mécanismes, et il lui faut l'observateur de lignes en place.
	 */
	if ( ! mouvementReduit() ) {
		initLignes();
	}

	initBlocs();
}
